import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useGridApiRef } from '@mui/x-data-grid';

import { colour } from './tokens';

/**
 * The columns every staff table shares, in the same order everywhere, so a person reading one list
 * finds things where they found them on the last:
 *
 *   Family (parent code under it, frozen) · Campus · Contact (both parents) · then the page's own.
 *
 * A list of students starts with the student, frozen, and then the same Family column.
 *
 * FREEZING. The installed data grid is the free edition, where pinning a column is a paid feature, so
 * the first column is held with CSS. The cells use position: sticky. The heading cannot: the grid
 * moves its whole heading row with a transform as you scroll, and sticky does nothing inside one. So
 * the heading is shifted back by the scroll distance, which the grid reports (useFrozenFirstColumn).
 * Checked in a browser: after scrolling a 2,800px table 1,500px right, the first heading and cells
 * stay where they started. Grids that freeze render every column (disableVirtualization), because
 * the grid otherwise drops columns scrolled out of view — the first one included. That is cheap at
 * these page sizes (at most 50 rows); re-measure if a larger page size is ever offered.
 *
 * UPGRADING THE GRID: the heading depends on two names in @mui/x-data-grid's API —
 * apiRef.current.subscribeEvent and its 'scrollPositionChange' event (checked against 6.20.4, the
 * installed version; package.json allows any 6.x). If either moves, the cells still freeze and only
 * the heading drifts (translateX falls back to 0px) — wrong to look at, never wrong about data.
 */

export const FROZEN = 'frozen-col';

const BACKGROUND = '#fff';

/** The styles that hold the first column. Spread into a grid's sx. */
export const frozenColumnSx = {
    [`& .MuiDataGrid-cell.${FROZEN}`]: {
        position: 'sticky',
        left: 0,
        zIndex: 2,
        bgcolor: BACKGROUND,
        borderRight: `1px solid ${colour.border}`,
    },
    [`& .MuiDataGrid-columnHeader.${FROZEN}`]: {
        transform: 'translateX(var(--frozen-left, 0px))',
        zIndex: 3,
        bgcolor: colour.stage,
        borderRight: `1px solid ${colour.border}`,
    },
};

/** An apiRef for the grid, keeping the frozen heading over its cells as the grid scrolls sideways. */
export const useFrozenFirstColumn = () => {
    const apiRef = useGridApiRef();
    useEffect(() => {
        if (!apiRef.current?.subscribeEvent) return undefined;
        return apiRef.current.subscribeEvent('scrollPositionChange', ({ left }) => {
            apiRef.current.rootElementRef?.current?.style.setProperty('--frozen-left', `${left || 0}px`);
        });
    }, [apiRef]);
    return apiRef;
};

const oneLine = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

/** A name with its reference dimmed underneath: a family and parent code, a student and their ID. */
export const NameAndCode = ({ name, code }) => (
    <Box sx={{ minWidth: 0, lineHeight: 1.25 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colour.body, ...oneLine }}>{name}</Typography>
        {code !== undefined && code !== null && code !== '' && (
            <Typography sx={{ fontSize: '11.5px', color: colour.faint, whiteSpace: 'nowrap' }}>{code}</Typography>
        )}
    </Box>
);

/**
 * The family, with the parent code under the name. AR asked for the code wherever the name appears:
 * the name is how you recognise a family, the code is what you type into TASS. Sorted, searched and
 * exported by the name alone; tables that export keep the code as its own column too.
 */
export const familyColumn = ({ field = 'family_name', name, code, frozen = true, minWidth = 180 } = {}) => ({
    field,
    headerName: 'Family',
    flex: 1,
    minWidth,
    ...(frozen ? { cellClassName: FROZEN, headerClassName: FROZEN } : {}),
    valueGetter: (params) => name(params.row) || String(code(params.row) ?? ''),
    renderCell: (params) => <NameAndCode name={params.value} code={code(params.row)} />,
});

/** A student first, frozen, for lists of students: their name, and their ID under it. */
export const studentColumn = ({ field = 'studentName', name, code, minWidth = 170 } = {}) => ({
    field,
    headerName: 'Student',
    flex: 1,
    minWidth,
    cellClassName: FROZEN,
    headerClassName: FROZEN,
    valueGetter: (params) => name(params.row) || String(code(params.row) ?? ''),
    renderCell: (params) => <NameAndCode name={params.value} code={code(params.row)} />,
});

/**
 * Both parents, both ways: phones on the first line, emails on the second.
 *
 * `phones` and `emails` are [{ value, isNew }]; an entry marked new reads "(new)" and is bolder, for a
 * detail the parent has just changed. Nothing held reads "NA", as the accounts columns do for an
 * absent value, rather than a blank that looks like it failed to load; `notHeldHint` says why, where
 * the reason is known.
 */
export const ContactLines = ({ phones = [], emails = [], notHeldHint }) => {
    const line = (list, size, weight) => {
        if (!list.length) {
            return <Typography title={notHeldHint} sx={{ fontSize: size, color: colour.faint, ...oneLine }}>NA</Typography>;
        }
        return (
            <Typography sx={{ fontSize: size, fontWeight: weight, color: colour.bodySoft, ...oneLine }}>
                {list.map((entry, index) => (
                    <React.Fragment key={`${entry.value}-${index}`}>
                        {index > 0 && ' · '}
                        <Box component="span" sx={entry.isNew ? { fontWeight: 700, color: colour.body } : undefined}>
                            {entry.isNew ? `${entry.value} (new)` : entry.value}
                        </Box>
                    </React.Fragment>
                ))}
            </Typography>
        );
    };
    return (
        <Box sx={{ minWidth: 0, lineHeight: 1.25 }}>
            {line(phones, '13px', 600)}
            {line(emails, '11.5px', 400)}
        </Box>
    );
};

/** The Contact column for a grid. `phones` and `emails` give a row's [{ value, isNew }]. */
export const contactColumn = ({ phones, emails, notHeldHint, minWidth = 210 } = {}) => ({
    field: 'contact',
    headerName: 'Contact',
    flex: 1,
    minWidth,
    sortable: false,
    disableExport: true,
    valueGetter: (params) => [...phones(params.row), ...emails(params.row)].map((entry) => entry.value).join(' · '),
    renderCell: (params) => (
        <ContactLines
            phones={phones(params.row)}
            emails={emails(params.row)}
            notHeldHint={notHeldHint ? notHeldHint(params.row) : undefined}
        />
    ),
});

/** Entries from plain values: blanks dropped, none marked new. */
export const entries = (...values) => values
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .map((value) => ({ value, isNew: false }));
